import {
    faCheck,
    faDownload,
    faFloppyDisk,
    faPlus,
    faRotateLeft,
    faTrash,
    faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    addToast,
    Button,
    ButtonGroup,
    Chip,
    Divider,
    Select,
    SelectItem,
    Slider,
} from "@heroui/react";
import { useQueryClient } from "@tanstack/react-query";
import * as turf from "@turf/turf";
import useRequest from "@/lib/request/useRequest";
import useAuth from "@/lib/auth/useAuth";
import type {
    CocoDocument,
    ImageryAnnotationRequest,
    ImageryAnnotationResponse,
    ImageryAnnotationSuggestionModel,
    ImageryAnnotationSuggestionRequest,
    ImageryAnnotationSuggestionResponse,
} from "@/types/imagery";
import {
    PointerEvent as ReactPointerEvent,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";

type Point = {
    x: number;
    y: number;
};

type PolygonAnnotation = {
    id: number;
    categoryId: number;
    points: Point[];
};

type ImageDimensions = {
    width: number;
    height: number;
};

type SuggestionFeedback = {
    color: "success" | "warning" | "danger";
    message: string;
};

type SuggestionRun = {
    token: number;
    imageId: number;
    model: ImageryAnnotationSuggestionModel;
    width: number;
    height: number;
    annotationRevision: number;
};

type VertexDrag = {
    pointerId: number;
    annotationId: number;
    pointIndex: number;
    originalPoints: Point[];
    latestPoints: Point[];
    hasMoved: boolean;
};

type SelectedVertex = {
    annotationId: number;
    pointIndex: number;
};

const COCO_CATEGORIES = [
    { id: 0, name: "objects", supercategory: "none", color: "#64748b" },
    {
        id: 1,
        name: "Artifact",
        supercategory: "objects",
        color: "#a855f7",
    },
    { id: 2, name: "Bare", supercategory: "objects", color: "#a16207" },
    { id: 3, name: "Cloud", supercategory: "objects", color: "#38bdf8" },
    { id: 4, name: "Crop", supercategory: "objects", color: "#22c55e" },
    { id: 5, name: "Haze", supercategory: "objects", color: "#94a3b8" },
    {
        id: 6,
        name: "Man-made structure",
        supercategory: "objects",
        color: "#f97316",
    },
    {
        id: 7,
        name: "Other vegetation",
        supercategory: "objects",
        color: "#84cc16",
    },
    { id: 8, name: "Shadow", supercategory: "objects", color: "#475569" },
    { id: 9, name: "Snow", supercategory: "objects", color: "#cbd5e1" },
    {
        id: 10,
        name: "Waterway",
        supercategory: "objects",
        color: "#2563eb",
    },
    {
        id: 11,
        name: "Active Irrigation",
        supercategory: "objects",
        color: "#06b6d4",
    },
];

const ANNOTATION_CATEGORIES = COCO_CATEGORIES.filter(
    (category) => category.id !== 0,
);
const CATEGORY_SHORTCUTS: Record<number, string> = {
    1: "1",
    2: "2",
    3: "3",
    4: "4",
    5: "5",
    6: "6",
    7: "7",
    8: "8",
    9: "9",
    10: "0",
    11: "I",
};
const STORED_ANNOTATION_INVALID_MESSAGE =
    "Saved annotations are invalid and must be replaced.";
const POLYGON_CLOSE_DISTANCE_PIXELS = 12;
const DEFAULT_CROP_CATEGORY_ID = 4;
const ANNOTATION_STALE_TIME_MS = 5 * 60 * 1000;
const MAX_ANNOTATIONS = 1_000;
const MAX_POLYGON_COORDINATES = 2_000;
const MAX_TOTAL_POLYGON_COORDINATES = 100_000;
const MIN_FILL_POLYGON_AREA = 1e-6;
const MIN_FILL_POLYGON_AREA_RATIO = 0.005;

function round(value: number, decimalPlaces = 2) {
    const scale = 10 ** decimalPlaces;
    return Math.round(value * scale) / scale;
}

function polygonArea(points: Point[]) {
    return (
        Math.abs(
            points.reduce((area, point, index) => {
                const nextPoint = points[(index + 1) % points.length];
                return area + point.x * nextPoint.y - nextPoint.x * point.y;
            }, 0),
        ) / 2
    );
}

function getRemainingPolygons(boundaries: Point[][], polygons: Point[][]) {
    if (boundaries.length === 0 || polygons.length === 0) return boundaries;

    const toMultiPolygon = (rings: Point[][]) =>
        turf.multiPolygon(
            rings.map((points) => [
                [...points, points[0]].map(({ x, y }) => [x, y]),
            ]),
        );
    const remaining = turf.difference(
        turf.featureCollection([
            toMultiPolygon(boundaries),
            toMultiPolygon(polygons),
        ]),
    );
    if (!remaining) return [];

    const pending = turf.flatten(remaining).features;
    const result: Point[][] = [];
    while (pending.length > 0) {
        const polygon = pending.pop()!;
        const rings = polygon.geometry.coordinates;
        if (rings.length === 1) {
            const points = rings[0].slice(0, -1).map(([x, y]) => ({ x, y }));
            if (polygonArea(points) > MIN_FILL_POLYGON_AREA) {
                result.push(points);
            }
            continue;
        }

        const lower = turf.bbox(polygon);
        const upper = [...lower] as typeof lower;
        const axis = lower[2] - lower[0] >= lower[3] - lower[1] ? 0 : 1;
        const holeBounds = turf.bbox(turf.polygon([rings[1]]));
        const midpoint = (holeBounds[axis] + holeBounds[axis + 2]) / 2;
        lower[axis + 2] = midpoint;
        upper[axis] = midpoint;
        const parts = [lower, upper].flatMap((bounds) => {
            const intersection = turf.intersect(
                turf.featureCollection([polygon, turf.bboxPolygon(bounds)]),
            );
            return intersection ? turf.flatten(intersection).features : [];
        });
        const remainingHoles = parts.reduce(
            (count, part) => count + part.geometry.coordinates.length - 1,
            0,
        );
        if (parts.length < 2 || remainingHoles >= rings.length - 1) {
            throw new Error("Could not split the remaining annotation area.");
        }
        pending.push(...parts);
    }
    return result;
}

function hasInvalidPolygonTopology(points: Point[]): boolean {
    const validationPoints = points.filter(
        (point, index) =>
            index === 0 ||
            point.x !== points[index - 1].x ||
            point.y !== points[index - 1].y,
    );
    const firstPoint = validationPoints[0];
    const lastPoint = validationPoints[validationPoints.length - 1];

    if (
        validationPoints.length > 1 &&
        firstPoint.x === lastPoint.x &&
        firstPoint.y === lastPoint.y
    ) {
        validationPoints.pop();
    }

    const closedCoordinates = [
        ...validationPoints.map(
            (point) => [point.x, point.y] as [number, number],
        ),
        [validationPoints[0].x, validationPoints[0].y] as [number, number],
    ];
    if (turf.kinks(turf.polygon([closedCoordinates])).features.length > 0) {
        return true;
    }

    const edgeCount = validationPoints.length;
    for (let firstIndex = 0; firstIndex < edgeCount; firstIndex += 1) {
        for (
            let secondIndex = firstIndex + 1;
            secondIndex < edgeCount;
            secondIndex += 1
        ) {
            const edgesAreAdjacent =
                secondIndex === firstIndex + 1 ||
                (firstIndex === 0 && secondIndex === edgeCount - 1);
            if (edgesAreAdjacent) {
                continue;
            }

            const firstEdge = turf.lineString([
                closedCoordinates[firstIndex],
                closedCoordinates[firstIndex + 1],
            ]);
            const secondEdge = turf.lineString([
                closedCoordinates[secondIndex],
                closedCoordinates[secondIndex + 1],
            ]);
            if (turf.lineOverlap(firstEdge, secondEdge).features.length > 0) {
                return true;
            }
        }
    }

    return false;
}

function polygonBoundingBox(points: Point[]): [number, number, number, number] {
    const xCoordinates = points.map((point) => point.x);
    const yCoordinates = points.map((point) => point.y);
    const minX = Math.min(...xCoordinates);
    const minY = Math.min(...yCoordinates);
    const maxX = Math.max(...xCoordinates);
    const maxY = Math.max(...yCoordinates);

    return [round(minX), round(minY), round(maxX - minX), round(maxY - minY)];
}

function pointsToSvg(points: Point[]) {
    return points.map((point) => `${point.x},${point.y}`).join(" ");
}

function findClosestEdgePoint(points: Point[], point: Point) {
    let closest:
        | { edgeIndex: number; point: Point; distanceSquared: number }
        | undefined;

    points.forEach((start, edgeIndex) => {
        const end = points[(edgeIndex + 1) % points.length];
        const deltaX = end.x - start.x;
        const deltaY = end.y - start.y;
        const lengthSquared = deltaX * deltaX + deltaY * deltaY;
        if (lengthSquared === 0) return;

        const distanceAlongEdge = Math.min(
            1,
            Math.max(
                0,
                ((point.x - start.x) * deltaX + (point.y - start.y) * deltaY) /
                    lengthSquared,
            ),
        );
        const projectedPoint = {
            x: round(start.x + distanceAlongEdge * deltaX),
            y: round(start.y + distanceAlongEdge * deltaY),
        };
        const distanceX = point.x - projectedPoint.x;
        const distanceY = point.y - projectedPoint.y;
        const distanceSquared = distanceX * distanceX + distanceY * distanceY;

        if (!closest || distanceSquared < closest.distanceSquared) {
            closest = { edgeIndex, point: projectedPoint, distanceSquared };
        }
    });

    return closest;
}

function getCategory(categoryId: number) {
    return (
        ANNOTATION_CATEGORIES.find((category) => category.id === categoryId) ??
        ANNOTATION_CATEGORIES[0]
    );
}

function findErrorMessage(value: unknown): string | null {
    if (typeof value === "string") return value;
    if (!value || typeof value !== "object") return null;

    for (const nestedValue of Object.values(value)) {
        const message = findErrorMessage(nestedValue);
        if (message) return message;
    }

    return null;
}

function getAnnotationErrorMessage(
    error: Error,
    fallback = "Annotations could not be saved.",
) {
    try {
        return findErrorMessage(JSON.parse(error.message)) ?? fallback;
    } catch {
        return error.message || fallback;
    }
}

function getAnnotationSignature(annotations: PolygonAnnotation[]) {
    return JSON.stringify(
        annotations.map((annotation) => ({
            categoryId: annotation.categoryId,
            points: annotation.points,
        })),
    );
}

function getSuggestedAnnotations(
    response: ImageryAnnotationSuggestionResponse,
): Omit<PolygonAnnotation, "id">[] | null {
    if (!Array.isArray(response.predictions)) return null;

    const suggestedAnnotations: Omit<PolygonAnnotation, "id">[] = [];
    for (const prediction of response.predictions) {
        if (
            prediction === null ||
            typeof prediction !== "object" ||
            !ANNOTATION_CATEGORIES.some(
                (category) => category.id === prediction.category_id,
            ) ||
            !Array.isArray(prediction.segmentation) ||
            prediction.segmentation.length < 6 ||
            prediction.segmentation.length % 2 !== 0 ||
            !prediction.segmentation.every(Number.isFinite)
        ) {
            return null;
        }

        const points: Point[] = [];
        for (
            let index = 0;
            index < prediction.segmentation.length;
            index += 2
        ) {
            const point = {
                x: prediction.segmentation[index],
                y: prediction.segmentation[index + 1],
            };
            if (
                point.x < 0 ||
                point.x > response.width ||
                point.y < 0 ||
                point.y > response.height
            ) {
                return null;
            }
            points.push(point);
        }

        const distinctPoints = new Set(
            points.map((point) => `${point.x},${point.y}`),
        );
        if (distinctPoints.size < 3 || polygonArea(points) === 0) return null;

        suggestedAnnotations.push({
            categoryId: prediction.category_id,
            points,
        });
    }

    return suggestedAnnotations;
}

function ImageryAnnotationEditor({
    imageId,
    imageUrl,
    nextImageId,
    overlayUrl,
    isDisabled = false,
    onDirtyChange,
    onSavingChange,
    onSaved,
}: {
    imageId: number;
    imageUrl: string;
    nextImageId?: number;
    overlayUrl?: string;
    isDisabled?: boolean;
    onDirtyChange?: (isDirty: boolean) => void;
    onSavingChange?: (isSaving: boolean) => void;
    onSaved?: () => void;
}) {
    const { axiosRequest, useGetRequest, useMutateRequest } = useRequest();
    const { user } = useAuth();
    const queryClient = useQueryClient();
    const annotationUrl = `imagery/${imageId}/annotations/`;
    const suggestionUrl = `imagery/${imageId}/annotation-suggestions/`;
    const nextAnnotationId = useRef(1);
    const annotationCanvasRef = useRef<SVGSVGElement | null>(null);
    const annotationRevision = useRef(0);
    const latestSuggestionRun = useRef(0);
    const activeSuggestionRun = useRef<SuggestionRun | null>(null);
    const vertexDrag = useRef<VertexDrag | null>(null);
    const imageIdRef = useRef(imageId);
    const imageDimensionsRef = useRef<ImageDimensions | null>(null);
    const selectedSuggestionModelRef =
        useRef<ImageryAnnotationSuggestionModel>("sparrow-one-light");
    const [imageDimensions, setImageDimensions] =
        useState<ImageDimensions | null>(null);
    const [imageLoadFailed, setImageLoadFailed] = useState(false);
    const [selectedCategoryId, setSelectedCategoryId] = useState(
        DEFAULT_CROP_CATEGORY_ID,
    );
    const [annotations, setAnnotations] = useState<PolygonAnnotation[]>([]);
    const [annotationOpacity, setAnnotationOpacity] = useState(0.3);
    const [draftPoints, setDraftPoints] = useState<Point[]>([]);
    const [isAddingPolygon, setIsAddingPolygon] = useState(false);
    const [previewPoint, setPreviewPoint] = useState<Point | null>(null);
    const [selectedAnnotationId, setSelectedAnnotationId] = useState<
        number | null
    >(null);
    const [selectedVertex, setSelectedVertex] = useState<SelectedVertex | null>(
        null,
    );
    const [hasStoredAnnotation, setHasStoredAnnotation] = useState(false);
    const [savedAnnotationSignature, setSavedAnnotationSignature] = useState<
        string | null
    >(null);
    const [selectedSuggestionModel, setSelectedSuggestionModel] =
        useState<ImageryAnnotationSuggestionModel>("sparrow-one-light");
    const [suggestionFeedback, setSuggestionFeedback] =
        useState<SuggestionFeedback | null>(null);

    imageIdRef.current = imageId;
    imageDimensionsRef.current = imageDimensions;
    selectedSuggestionModelRef.current = selectedSuggestionModel;

    useEffect(() => {
        setImageLoadFailed(false);
    }, [imageUrl]);

    const markAnnotationRevision = useCallback(() => {
        annotationRevision.current += 1;
    }, []);

    const hydrateAnnotations = useCallback((coco: CocoDocument | null) => {
        const hydratedAnnotations = (coco?.annotations ?? []).flatMap(
            (annotation) => {
                const segmentation = annotation.segmentation[0];
                if (!segmentation || segmentation.length < 6) return [];

                const points: Point[] = [];
                for (let index = 0; index < segmentation.length; index += 2) {
                    points.push({
                        x: segmentation[index],
                        y: segmentation[index + 1],
                    });
                }

                return [
                    {
                        id: annotation.id,
                        categoryId: annotation.category_id,
                        points,
                    },
                ];
            },
        );

        setAnnotations(hydratedAnnotations);
        setDraftPoints([]);
        setIsAddingPolygon(false);
        setPreviewPoint(null);
        setSelectedAnnotationId(null);
        setSelectedVertex(null);
        nextAnnotationId.current =
            Math.max(0, ...hydratedAnnotations.map(({ id }) => id)) + 1;

        return hydratedAnnotations;
    }, []);

    useEffect(() => {
        latestSuggestionRun.current += 1;
        activeSuggestionRun.current = null;
        setSuggestionFeedback(null);

        return () => {
            latestSuggestionRun.current += 1;
            activeSuggestionRun.current = null;
        };
    }, [imageId]);

    const currentAnnotationSignature = useMemo(
        () => getAnnotationSignature(annotations),
        [annotations],
    );
    const hasUnsavedChanges =
        savedAnnotationSignature !== null &&
        (currentAnnotationSignature !== savedAnnotationSignature ||
            draftPoints.length > 0);

    useEffect(() => {
        onDirtyChange?.(hasUnsavedChanges);
    }, [hasUnsavedChanges, onDirtyChange]);

    useEffect(() => {
        return () => onDirtyChange?.(false);
    }, [onDirtyChange]);

    const annotationQuery = useGetRequest<ImageryAnnotationResponse>({
        url: annotationUrl,
        queryOptions: {
            retry: false,
            refetchOnWindowFocus: false,
            staleTime: ANNOTATION_STALE_TIME_MS,
        },
    });
    useEffect(() => {
        if (!annotationQuery.isSuccess || nextImageId === undefined) return;

        const nextAnnotationUrl = `imagery/${nextImageId}/annotations/`;
        void queryClient.prefetchQuery({
            queryKey: [nextAnnotationUrl],
            queryFn: () =>
                axiosRequest<ImageryAnnotationResponse>({
                    url: nextAnnotationUrl,
                }),
            retry: false,
            staleTime: ANNOTATION_STALE_TIME_MS,
        });
    }, [annotationQuery.isSuccess, axiosRequest, nextImageId, queryClient]);
    const isStoredAnnotationInvalid =
        annotationQuery.isError &&
        annotationQuery.error.message === STORED_ANNOTATION_INVALID_MESSAGE;
    const hasAnnotationLoadError =
        annotationQuery.isError && !isStoredAnnotationInvalid;
    const validDataBoundary = annotationQuery.data?.valid_data_boundary;
    const validDataPolygons = useMemo(
        () =>
            (validDataBoundary ?? []).map((segmentation) => {
                const points: Point[] = [];
                for (let index = 0; index < segmentation.length; index += 2) {
                    points.push({
                        x: segmentation[index],
                        y: segmentation[index + 1],
                    });
                }
                return points;
            }),
        [validDataBoundary],
    );

    const constrainPointToValidData = useCallback(
        (point: Point) => {
            if (validDataPolygons.length === 0) return point;

            const pointFeature = turf.point([point.x, point.y]);
            const isWithinValidData = validDataPolygons.some((polygon) => {
                const firstPoint = polygon[0];
                return turf.booleanPointInPolygon(
                    pointFeature,
                    turf.polygon([
                        [
                            ...polygon.map(
                                ({ x, y }) => [x, y] as [number, number],
                            ),
                            [firstPoint.x, firstPoint.y],
                        ],
                    ]),
                );
            });
            if (isWithinValidData) return point;

            let constrainedPoint = point;
            let closestDistanceSquared = Number.POSITIVE_INFINITY;
            validDataPolygons.forEach((polygon) => {
                const closest = findClosestEdgePoint(polygon, point);
                if (
                    closest &&
                    closest.distanceSquared < closestDistanceSquared
                ) {
                    constrainedPoint = closest.point;
                    closestDistanceSquared = closest.distanceSquared;
                }
            });
            return constrainedPoint;
        },
        [validDataPolygons],
    );

    const saveAnnotationRequest = useMutateRequest<ImageryAnnotationResponse>({
        method: "put",
        url: annotationUrl,
        successMessage: "Annotations saved.",
        onError: (error) => {
            addToast({
                title: getAnnotationErrorMessage(error),
                timeout: 3000,
                color: "danger",
            });
        },
        onSuccess: (response) => {
            queryClient.setQueryData<ImageryAnnotationResponse>(
                [annotationUrl],
                response,
            );
            const hydratedAnnotations = hydrateAnnotations(response.annotation);
            setSuggestionFeedback(null);
            setHasStoredAnnotation(response.annotation !== null);
            setSavedAnnotationSignature(
                getAnnotationSignature(hydratedAnnotations),
            );
            onSaved?.();
        },
    });

    const suggestionRequest =
        useMutateRequest<ImageryAnnotationSuggestionResponse>({
            method: "post",
            url: suggestionUrl,
            onError: (error) => {
                const run = activeSuggestionRun.current;
                if (!run || run.token !== latestSuggestionRun.current) return;

                activeSuggestionRun.current = null;
                setSuggestionFeedback({
                    color: "danger",
                    message: getAnnotationErrorMessage(
                        error,
                        "Segments could not be generated.",
                    ),
                });
            },
            onSuccess: (response) => {
                const run = activeSuggestionRun.current;
                if (!run || run.token !== latestSuggestionRun.current) return;

                activeSuggestionRun.current = null;
                const currentDimensions = imageDimensionsRef.current;
                if (
                    imageIdRef.current !== run.imageId ||
                    selectedSuggestionModelRef.current !== run.model ||
                    !currentDimensions ||
                    currentDimensions.width !== run.width ||
                    currentDimensions.height !== run.height ||
                    response.model !== run.model ||
                    response.image_id !== run.imageId ||
                    response.width !== run.width ||
                    response.height !== run.height
                ) {
                    setSuggestionFeedback({
                        color: "warning",
                        message:
                            "The image changed before generation finished. Generate again.",
                    });
                    return;
                }

                if (annotationRevision.current !== run.annotationRevision) {
                    setSuggestionFeedback({
                        color: "warning",
                        message:
                            "Annotations changed before generation finished. Generate again to replace them.",
                    });
                    return;
                }

                const suggestedAnnotations = getSuggestedAnnotations(response);
                if (suggestedAnnotations === null) {
                    setSuggestionFeedback({
                        color: "danger",
                        message: "Generated segments could not be applied.",
                    });
                    return;
                }
                if (suggestedAnnotations.length === 0) {
                    setSuggestionFeedback({
                        color: "warning",
                        message: "No segments were found for this image.",
                    });
                    return;
                }

                const generatedAnnotations = suggestedAnnotations.map(
                    (annotation, index) => ({
                        ...annotation,
                        id: index + 1,
                    }),
                );
                markAnnotationRevision();
                setAnnotations(generatedAnnotations);
                setDraftPoints([]);
                setIsAddingPolygon(false);
                setPreviewPoint(null);
                setSelectedAnnotationId(generatedAnnotations[0].id);
                setSelectedVertex(null);
                nextAnnotationId.current = generatedAnnotations.length + 1;
                setSuggestionFeedback({
                    color: "success",
                    message: `${generatedAnnotations.length} segment${
                        generatedAnnotations.length === 1 ? "" : "s"
                    } generated. Review and save when ready.`,
                });
            },
        });

    useEffect(() => {
        onSavingChange?.(saveAnnotationRequest.isPending);
    }, [onSavingChange, saveAnnotationRequest.isPending]);

    useEffect(() => {
        return () => onSavingChange?.(false);
    }, [onSavingChange]);

    useEffect(() => {
        if (
            !isStoredAnnotationInvalid ||
            savedAnnotationSignature !== null ||
            saveAnnotationRequest.isPending
        ) {
            return;
        }

        const hydratedAnnotations = hydrateAnnotations(null);
        setHasStoredAnnotation(false);
        setSavedAnnotationSignature(
            getAnnotationSignature(hydratedAnnotations),
        );
    }, [
        hydrateAnnotations,
        isStoredAnnotationInvalid,
        saveAnnotationRequest.isPending,
        savedAnnotationSignature,
    ]);

    useEffect(() => {
        if (
            !annotationQuery.isSuccess ||
            hasUnsavedChanges ||
            saveAnnotationRequest.isPending
        ) {
            return;
        }

        const hydratedAnnotations = hydrateAnnotations(
            annotationQuery.data.annotation,
        );
        setHasStoredAnnotation(annotationQuery.data.annotation !== null);
        setSavedAnnotationSignature(
            getAnnotationSignature(hydratedAnnotations),
        );
    }, [
        annotationQuery.data,
        annotationQuery.isSuccess,
        hasUnsavedChanges,
        hydrateAnnotations,
        saveAnnotationRequest.isPending,
    ]);

    const selectedCategory = useMemo(
        () => getCategory(selectedCategoryId),
        [selectedCategoryId],
    );
    const selectedAnnotation = useMemo(
        () =>
            annotations.find(
                (annotation) => annotation.id === selectedAnnotationId,
            ) ?? null,
        [annotations, selectedAnnotationId],
    );

    const isEditingDisabled =
        isDisabled ||
        annotationQuery.isPending ||
        annotationQuery.isFetching ||
        hasAnnotationLoadError ||
        saveAnnotationRequest.isPending;

    const generateSegments = useCallback(() => {
        if (
            !imageDimensions ||
            isEditingDisabled ||
            isAddingPolygon ||
            suggestionRequest.isPending
        ) {
            return;
        }

        const token = latestSuggestionRun.current + 1;
        latestSuggestionRun.current = token;
        activeSuggestionRun.current = {
            token,
            imageId,
            model: selectedSuggestionModel,
            width: imageDimensions.width,
            height: imageDimensions.height,
            annotationRevision: annotationRevision.current,
        };
        setSuggestionFeedback(null);
        const request: ImageryAnnotationSuggestionRequest = {
            model: selectedSuggestionModel,
        };
        suggestionRequest.mutate({ data: request });
    }, [
        imageDimensions,
        imageId,
        isAddingPolygon,
        isEditingDisabled,
        selectedSuggestionModel,
        suggestionRequest,
    ]);

    const pointFromClientPosition = useCallback(
        (clientX: number, clientY: number, clampToImage = false) => {
            if (!imageDimensions || !annotationCanvasRef.current) return null;

            const bounds = annotationCanvasRef.current.getBoundingClientRect();
            if (bounds.width === 0 || bounds.height === 0) return null;

            const scale = Math.min(
                bounds.width / imageDimensions.width,
                bounds.height / imageDimensions.height,
            );
            const renderedWidth = imageDimensions.width * scale;
            const renderedHeight = imageDimensions.height * scale;
            const offsetX = (bounds.width - renderedWidth) / 2;
            const offsetY = (bounds.height - renderedHeight) / 2;
            let renderedX = clientX - bounds.left - offsetX;
            let renderedY = clientY - bounds.top - offsetY;

            if (
                !clampToImage &&
                (renderedX < 0 ||
                    renderedY < 0 ||
                    renderedX > renderedWidth ||
                    renderedY > renderedHeight)
            ) {
                return null;
            }

            renderedX = Math.min(renderedWidth, Math.max(0, renderedX));
            renderedY = Math.min(renderedHeight, Math.max(0, renderedY));

            return {
                x: round(renderedX / scale),
                y: round(renderedY / scale),
            };
        },
        [imageDimensions],
    );

    const pointFromEvent = useCallback(
        (event: ReactPointerEvent<SVGSVGElement>) =>
            pointFromClientPosition(event.clientX, event.clientY),
        [pointFromClientPosition],
    );

    const validDataPointFromEvent = useCallback(
        (event: ReactPointerEvent<SVGSVGElement>) => {
            const point = pointFromClientPosition(
                event.clientX,
                event.clientY,
                true,
            );
            return point ? constrainPointToValidData(point) : null;
        },
        [constrainPointToValidData, pointFromClientPosition],
    );

    const isNearDraftStart = useCallback(
        (event: ReactPointerEvent<SVGSVGElement>) => {
            if (
                draftPoints.length < 3 ||
                !imageDimensions ||
                !annotationCanvasRef.current
            ) {
                return false;
            }

            const point = pointFromEvent(event);
            if (!point) return false;
            const bounds = annotationCanvasRef.current.getBoundingClientRect();
            const scale = Math.min(
                bounds.width / imageDimensions.width,
                bounds.height / imageDimensions.height,
            );
            const firstPoint = draftPoints[0];

            return (
                Math.hypot(point.x - firstPoint.x, point.y - firstPoint.y) *
                    scale <=
                POLYGON_CLOSE_DISTANCE_PIXELS
            );
        },
        [draftPoints, imageDimensions, pointFromEvent],
    );

    const finishPolygon = useCallback(() => {
        const distinctPoints = new Set(
            draftPoints.map((point) => `${point.x},${point.y}`),
        );
        if (distinctPoints.size < 3 || polygonArea(draftPoints) === 0) {
            addToast({
                title: "A polygon needs at least three non-collinear points.",
                color: "danger",
                timeout: 3000,
            });
            return;
        }
        if (hasInvalidPolygonTopology(draftPoints)) {
            addToast({
                title: "A polygon cannot have crossing, touching, or overlapping non-adjacent edges.",
                color: "danger",
                timeout: 3000,
            });
            return;
        }

        const annotationId = nextAnnotationId.current;
        nextAnnotationId.current += 1;
        markAnnotationRevision();
        setAnnotations((currentAnnotations) => [
            ...currentAnnotations,
            {
                id: annotationId,
                categoryId: selectedCategoryId,
                points: draftPoints,
            },
        ]);
        setSelectedAnnotationId(annotationId);
        setSelectedVertex(null);
        setDraftPoints([]);
        setIsAddingPolygon(false);
        setPreviewPoint(null);
        annotationCanvasRef.current?.focus();
    }, [draftPoints, markAnnotationRevision, selectedCategoryId]);

    const cancelPolygon = useCallback(() => {
        if (draftPoints.length > 0) {
            markAnnotationRevision();
        }
        setDraftPoints([]);
        setIsAddingPolygon(false);
        setPreviewPoint(null);
        setSelectedVertex(null);
    }, [draftPoints.length, markAnnotationRevision]);

    const startPolygon = useCallback(() => {
        setSelectedAnnotationId(null);
        setSelectedVertex(null);
        setDraftPoints([]);
        setPreviewPoint(null);
        setIsAddingPolygon(true);
        annotationCanvasRef.current?.focus();
    }, []);

    const addPolygons = useCallback(
        (polygons: Point[][]) => {
            if (!imageDimensions || polygons.length === 0) return;

            const addedAnnotations = polygons.map((points) => {
                const annotationId = nextAnnotationId.current;
                nextAnnotationId.current += 1;
                return {
                    id: annotationId,
                    categoryId: selectedCategoryId,
                    points,
                };
            });
            markAnnotationRevision();
            setAnnotations((currentAnnotations) => [
                ...currentAnnotations,
                ...addedAnnotations,
            ]);
            setSelectedAnnotationId(addedAnnotations[0].id);
            setSelectedVertex(null);
            annotationCanvasRef.current?.focus();
        },
        [imageDimensions, markAnnotationRevision, selectedCategoryId],
    );

    const fillRemainingSpace = useCallback(() => {
        const existingPolygons = annotations.map(({ points }) => points);
        const minimumArea =
            validDataPolygons.reduce(
                (area, points) => area + polygonArea(points),
                0,
            ) * MIN_FILL_POLYGON_AREA_RATIO;
        const remainingPolygons = getRemainingPolygons(
            validDataPolygons,
            existingPolygons,
        ).filter((points) => polygonArea(points) >= minimumArea);
        const allPolygons = [...existingPolygons, ...remainingPolygons];
        if (
            allPolygons.length > MAX_ANNOTATIONS ||
            allPolygons.some(
                (points) => points.length * 2 > MAX_POLYGON_COORDINATES,
            ) ||
            allPolygons.reduce(
                (count, points) => count + points.length * 2,
                0,
            ) > MAX_TOTAL_POLYGON_COORDINATES
        ) {
            addToast({
                title: "The remaining space is too complex to fill. Simplify or remove polygons first.",
                color: "warning",
                timeout: 3000,
            });
            return;
        }
        addPolygons(remainingPolygons);
    }, [addPolygons, annotations, validDataPolygons]);

    const deleteSelectedAnnotation = useCallback(() => {
        if (selectedAnnotationId === null) return;
        markAnnotationRevision();
        setAnnotations((currentAnnotations) =>
            currentAnnotations.filter(
                (annotation) => annotation.id !== selectedAnnotationId,
            ),
        );
        setSelectedAnnotationId(null);
        setSelectedVertex(null);
    }, [markAnnotationRevision, selectedAnnotationId]);

    const clearAnnotations = useCallback(() => {
        if (annotations.length === 0 && draftPoints.length === 0) return;
        markAnnotationRevision();
        setAnnotations([]);
        setDraftPoints([]);
        setIsAddingPolygon(false);
        setPreviewPoint(null);
        setSelectedAnnotationId(null);
        setSelectedVertex(null);
        nextAnnotationId.current = 1;
    }, [annotations.length, draftPoints.length, markAnnotationRevision]);

    const selectAnnotation = useCallback((annotation: PolygonAnnotation) => {
        setIsAddingPolygon(false);
        setSelectedAnnotationId(annotation.id);
        setSelectedVertex(null);
        annotationCanvasRef.current?.focus();
    }, []);

    const insertVertex = useCallback(
        (annotation: PolygonAnnotation, point: Point) => {
            const closestEdge = findClosestEdgePoint(annotation.points, point);
            if (!closestEdge) return;

            const edgeStart = annotation.points[closestEdge.edgeIndex];
            const edgeEnd =
                annotation.points[
                    (closestEdge.edgeIndex + 1) % annotation.points.length
                ];
            if (
                (closestEdge.point.x === edgeStart.x &&
                    closestEdge.point.y === edgeStart.y) ||
                (closestEdge.point.x === edgeEnd.x &&
                    closestEdge.point.y === edgeEnd.y)
            ) {
                return;
            }

            const updatedPoints = [
                ...annotation.points.slice(0, closestEdge.edgeIndex + 1),
                closestEdge.point,
                ...annotation.points.slice(closestEdge.edgeIndex + 1),
            ];
            if (hasInvalidPolygonTopology(updatedPoints)) return;

            markAnnotationRevision();
            setAnnotations((currentAnnotations) =>
                currentAnnotations.map((currentAnnotation) =>
                    currentAnnotation.id === annotation.id
                        ? { ...currentAnnotation, points: updatedPoints }
                        : currentAnnotation,
                ),
            );
            const pointIndex = closestEdge.edgeIndex + 1;
            setSelectedVertex({ annotationId: annotation.id, pointIndex });
            return { pointIndex, points: updatedPoints };
        },
        [markAnnotationRevision],
    );

    const deleteSelectedVertex = useCallback(() => {
        if (!selectedVertex || vertexDrag.current) return;

        const annotation = annotations.find(
            ({ id }) => id === selectedVertex.annotationId,
        );
        if (
            !annotation ||
            annotation.id !== selectedAnnotationId ||
            selectedVertex.pointIndex < 0 ||
            selectedVertex.pointIndex >= annotation.points.length
        ) {
            setSelectedVertex(null);
            return;
        }

        const updatedPoints = annotation.points.filter(
            (_, pointIndex) => pointIndex !== selectedVertex.pointIndex,
        );
        const distinctPoints = new Set(
            updatedPoints.map((point) => `${point.x},${point.y}`),
        );
        if (distinctPoints.size < 3 || polygonArea(updatedPoints) === 0) {
            addToast({
                title: "A polygon needs at least three non-collinear points.",
                color: "danger",
                timeout: 3000,
            });
            return;
        }
        if (hasInvalidPolygonTopology(updatedPoints)) {
            addToast({
                title: "A polygon cannot have crossing, touching, or overlapping non-adjacent edges.",
                color: "danger",
                timeout: 3000,
            });
            return;
        }

        markAnnotationRevision();
        setAnnotations((currentAnnotations) =>
            currentAnnotations.map((currentAnnotation) =>
                currentAnnotation.id === annotation.id
                    ? { ...currentAnnotation, points: updatedPoints }
                    : currentAnnotation,
            ),
        );
        setSelectedVertex(null);
    }, [
        annotations,
        markAnnotationRevision,
        selectedAnnotationId,
        selectedVertex,
    ]);

    const changeSelectedCategory = useCallback(
        (categoryId: number) => {
            if (draftPoints.length > 0 && selectedCategoryId !== categoryId) {
                markAnnotationRevision();
            }
            setSelectedCategoryId(categoryId);
        },
        [draftPoints.length, markAnnotationRevision, selectedCategoryId],
    );

    const changeAnnotationCategory = useCallback(
        (annotationId: number, categoryId: number) => {
            const annotation = annotations.find(
                (annotation) => annotation.id === annotationId,
            );
            if (!annotation || annotation.categoryId === categoryId) {
                return;
            }

            markAnnotationRevision();
            setAnnotations((currentAnnotations) =>
                currentAnnotations.map((annotation) =>
                    annotation.id === annotationId
                        ? { ...annotation, categoryId }
                        : annotation,
                ),
            );
        },
        [annotations, markAnnotationRevision],
    );

    const moveVertex = useCallback(
        (event: ReactPointerEvent<SVGElement>) => {
            const drag = vertexDrag.current;
            if (!drag || drag.pointerId !== event.pointerId) return;

            event.stopPropagation();
            const imagePoint = pointFromClientPosition(
                event.clientX,
                event.clientY,
                true,
            );
            if (!imagePoint) return;
            const point = constrainPointToValidData(imagePoint);
            const currentPoint = drag.latestPoints[drag.pointIndex];
            if (currentPoint.x === point.x && currentPoint.y === point.y)
                return;

            if (!drag.hasMoved) {
                drag.hasMoved = true;
                markAnnotationRevision();
            }
            const updatedPoints = drag.latestPoints.map(
                (currentPoint, index) =>
                    index === drag.pointIndex ? point : currentPoint,
            );
            drag.latestPoints = updatedPoints;
            setAnnotations((currentAnnotations) =>
                currentAnnotations.map((annotation) =>
                    annotation.id === drag.annotationId
                        ? { ...annotation, points: updatedPoints }
                        : annotation,
                ),
            );
        },
        [
            constrainPointToValidData,
            markAnnotationRevision,
            pointFromClientPosition,
        ],
    );

    const finishVertexDrag = useCallback(
        (event: ReactPointerEvent<SVGElement>, cancel = false) => {
            const drag = vertexDrag.current;
            if (!drag || drag.pointerId !== event.pointerId) return;

            event.stopPropagation();
            if (event.currentTarget.hasPointerCapture(event.pointerId)) {
                event.currentTarget.releasePointerCapture(event.pointerId);
            }
            vertexDrag.current = null;
            if (!drag.hasMoved) return;

            const distinctPoints = new Set(
                drag.latestPoints.map((point) => `${point.x},${point.y}`),
            );
            const isDegenerate =
                distinctPoints.size < 3 || polygonArea(drag.latestPoints) === 0;
            const hasInvalidTopology =
                !isDegenerate && hasInvalidPolygonTopology(drag.latestPoints);
            if (!cancel && !isDegenerate && !hasInvalidTopology) return;

            setAnnotations((currentAnnotations) =>
                currentAnnotations.map((annotation) =>
                    annotation.id === drag.annotationId
                        ? { ...annotation, points: drag.originalPoints }
                        : annotation,
                ),
            );
            if (!cancel) {
                addToast({
                    title: isDegenerate
                        ? "A polygon needs at least three non-collinear points."
                        : "A polygon cannot have crossing, touching, or overlapping non-adjacent edges.",
                    color: "danger",
                    timeout: 3000,
                });
            }
        },
        [],
    );

    const buildCoco = useCallback((): CocoDocument | null => {
        if (!imageDimensions) return null;

        return {
            info: {
                description: `Manual annotations for Sentinel imagery ${imageId}`,
                date_created: new Date().toISOString(),
                annotated_by_user_id: user?.id ?? null,
            },
            categories: COCO_CATEGORIES.map((category) => ({
                id: category.id,
                name: category.name,
                supercategory: category.supercategory,
            })),
            images: [
                {
                    id: imageId,
                    width: imageDimensions.width,
                    height: imageDimensions.height,
                    file_name: `${imageId}_RGB.png`,
                },
            ],
            annotations: annotations.map((annotation, index) => ({
                id: index + 1,
                image_id: imageId,
                category_id: annotation.categoryId,
                segmentation: [
                    annotation.points.flatMap((point) => [point.x, point.y]),
                ],
                area: round(polygonArea(annotation.points), 4),
                bbox: polygonBoundingBox(annotation.points),
                iscrowd: 0,
                attributes: {
                    source: "manual",
                },
            })),
        };
    }, [annotations, imageDimensions, imageId, user?.id]);

    const downloadCoco = useCallback(() => {
        const coco = buildCoco();
        if (!coco || coco.annotations.length === 0) return;

        const objectUrl = window.URL.createObjectURL(
            new Blob([JSON.stringify(coco, null, 2)], {
                type: "application/json",
            }),
        );
        const link = document.createElement("a");
        link.href = objectUrl;
        link.download = `sentinel-imagery-${imageId}-coco.json`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(objectUrl);
    }, [buildCoco, imageId]);

    const saveAnnotations = useCallback(() => {
        if (annotations.length === 0) return;

        const request: ImageryAnnotationRequest = {
            annotations: annotations.map((annotation) => ({
                category_id: annotation.categoryId,
                segmentation: annotation.points.flatMap((point) => [
                    point.x,
                    point.y,
                ]),
            })),
        };
        saveAnnotationRequest.mutate({
            data: request,
        });
    }, [annotations, saveAnnotationRequest]);

    const renderSaveAnnotationsButton = (className?: string) => (
        <Button
            size="sm"
            color="primary"
            className={className}
            isLoading={saveAnnotationRequest.isPending}
            isDisabled={
                annotations.length === 0 ||
                imageDimensions === null ||
                isEditingDisabled ||
                suggestionRequest.isPending ||
                isAddingPolygon ||
                (!hasUnsavedChanges && hasStoredAnnotation)
            }
            onPress={saveAnnotations}
            startContent={
                saveAnnotationRequest.isPending ? null : (
                    <FontAwesomeIcon icon={faFloppyDisk} />
                )
            }
        >
            Save annotations
        </Button>
    );

    const vertexRadius = imageDimensions
        ? Math.max(
              2,
              Math.min(imageDimensions.width, imageDimensions.height) * 0.006,
          )
        : 2;

    const categoryOptions = ANNOTATION_CATEGORIES.map((category) => (
        <SelectItem
            key={String(category.id)}
            endContent={
                <span className="text-xs text-default-400">
                    {CATEGORY_SHORTCUTS[category.id]}
                </span>
            }
            startContent={
                <span
                    className="h-3 w-3 rounded-full"
                    style={{ backgroundColor: category.color }}
                />
            }
        >
            {category.name}
        </SelectItem>
    ));

    return (
        <div className="grid h-full min-h-0 w-full min-w-0 gap-2 lg:grid-cols-[14rem_minmax(0,1fr)] lg:grid-rows-[auto_minmax(25rem,1fr)]">
            <div className="rounded-lg border border-default-200 bg-content1 shadow-sm lg:col-span-2">
                <div className="flex flex-wrap items-center gap-2 p-2">
                    <Select
                        label="New polygon type"
                        size="sm"
                        variant="bordered"
                        className="w-44"
                        disallowEmptySelection
                        isDisabled={isEditingDisabled}
                        selectedKeys={[String(selectedCategoryId)]}
                        startContent={
                            <span
                                className="h-2.5 w-2.5 shrink-0 rounded-full"
                                style={{
                                    backgroundColor: selectedCategory.color,
                                }}
                            />
                        }
                        onChange={(event) => {
                            const categoryId = Number(event.target.value);
                            if (Number.isInteger(categoryId)) {
                                changeSelectedCategory(categoryId);
                            }
                        }}
                    >
                        {categoryOptions}
                    </Select>

                    <Divider orientation="vertical" className="mx-1 h-6" />

                    <ButtonGroup size="sm" variant="flat" className="flex-1">
                        <Button
                            className="flex-1"
                            color="success"
                            isDisabled={
                                isEditingDisabled ||
                                !isAddingPolygon ||
                                draftPoints.length < 3
                            }
                            onPress={finishPolygon}
                            startContent={<FontAwesomeIcon icon={faCheck} />}
                        >
                            Finish
                        </Button>
                        <Button
                            className="flex-1"
                            isDisabled={
                                isEditingDisabled ||
                                !isAddingPolygon ||
                                draftPoints.length === 0
                            }
                            onPress={() => {
                                markAnnotationRevision();
                                setDraftPoints((currentPoints) =>
                                    currentPoints.slice(0, -1),
                                );
                            }}
                            startContent={
                                <FontAwesomeIcon icon={faRotateLeft} />
                            }
                        >
                            Undo
                        </Button>
                        <Button
                            className="flex-1"
                            isDisabled={isEditingDisabled || !isAddingPolygon}
                            onPress={cancelPolygon}
                            startContent={<FontAwesomeIcon icon={faXmark} />}
                        >
                            Cancel
                        </Button>
                    </ButtonGroup>

                    <Divider orientation="vertical" className="mx-1 h-6" />

                    <Select
                        aria-label="Annotation model"
                        size="sm"
                        variant="bordered"
                        className="w-40"
                        disallowEmptySelection
                        isDisabled={
                            isEditingDisabled || suggestionRequest.isPending
                        }
                        selectedKeys={[selectedSuggestionModel]}
                        onChange={(event) => {
                            const model = event.target.value;
                            if (
                                model === "sparrow-one" ||
                                model === "sparrow-one-light"
                            ) {
                                setSelectedSuggestionModel(model);
                            }
                        }}
                    >
                        <SelectItem key="sparrow-one">Sparrow</SelectItem>
                        <SelectItem key="sparrow-one-light">
                            Sparrow Light
                        </SelectItem>
                    </Select>

                    <Button
                        size="sm"
                        variant="flat"
                        isLoading={suggestionRequest.isPending}
                        isDisabled={
                            imageDimensions === null ||
                            isEditingDisabled ||
                            isAddingPolygon ||
                            suggestionRequest.isPending
                        }
                        onPress={generateSegments}
                    >
                        Generate segments
                    </Button>

                    {renderSaveAnnotationsButton()}
                </div>

                <div className="flex flex-wrap items-center justify-between gap-2 border-t border-default-100 px-2 py-1.5">
                    <div className="flex flex-wrap items-center gap-2">
                        {annotationQuery.isFetching ? (
                            <Chip size="sm" variant="flat">
                                Loading saved annotations…
                            </Chip>
                        ) : isStoredAnnotationInvalid ? (
                            <>
                                <Chip size="sm" color="warning" variant="flat">
                                    Saved annotations invalid
                                </Chip>
                                <span className="text-xs text-warning-500">
                                    Saving new annotations will replace them.
                                </span>
                            </>
                        ) : hasAnnotationLoadError ? (
                            <Chip size="sm" color="danger" variant="flat">
                                Annotations could not be loaded
                            </Chip>
                        ) : (
                            <Chip
                                size="sm"
                                color={
                                    hasUnsavedChanges
                                        ? "warning"
                                        : hasStoredAnnotation
                                          ? "success"
                                          : "default"
                                }
                                variant="flat"
                            >
                                {hasUnsavedChanges
                                    ? "Unsaved changes"
                                    : hasStoredAnnotation
                                      ? "Saved"
                                      : "Not saved"}
                            </Chip>
                        )}
                        {suggestionFeedback && (
                            <Chip
                                size="sm"
                                color={suggestionFeedback.color}
                                variant="flat"
                            >
                                {suggestionFeedback.message}
                            </Chip>
                        )}
                    </div>
                    <div className="flex flex-wrap items-center gap-1">
                        <Slider
                            label="Fill opacity"
                            size="sm"
                            minValue={0}
                            maxValue={1}
                            step={0.01}
                            formatOptions={{ style: "percent" }}
                            value={annotationOpacity}
                            onChange={(value) =>
                                setAnnotationOpacity(value as number)
                            }
                            className="mr-2 w-40"
                        />
                        <Button
                            size="sm"
                            variant="light"
                            isDisabled={
                                annotations.length === 0 ||
                                imageDimensions === null ||
                                saveAnnotationRequest.isPending
                            }
                            onPress={downloadCoco}
                            startContent={<FontAwesomeIcon icon={faDownload} />}
                        >
                            Download COCO
                        </Button>
                        <Button
                            size="sm"
                            color="danger"
                            variant="light"
                            isDisabled={
                                isEditingDisabled ||
                                selectedAnnotationId === null
                            }
                            onPress={deleteSelectedAnnotation}
                            startContent={<FontAwesomeIcon icon={faTrash} />}
                        >
                            Delete selected
                        </Button>
                        <Button
                            size="sm"
                            color="danger"
                            variant="light"
                            isDisabled={
                                isEditingDisabled ||
                                (annotations.length === 0 &&
                                    draftPoints.length === 0)
                            }
                            onPress={clearAnnotations}
                        >
                            Clear all
                        </Button>
                    </div>
                </div>
            </div>

            <aside className="flex h-full min-h-[25rem] flex-col overflow-hidden rounded-lg border border-default-200 bg-content1 shadow-sm">
                <div className="flex items-center justify-between border-b border-default-100 px-3 py-2">
                    <span className="text-sm font-semibold">Polygons</span>
                    <Chip size="sm" variant="flat">
                        {annotations.length}
                    </Chip>
                </div>
                <div className="border-b border-default-100 px-3 py-2 text-xs">
                    {isAddingPolygon ? (
                        <>
                            <p className="font-semibold text-primary">
                                Adding a new polygon
                            </p>
                            <p className="text-default-500">
                                Add points on the image, then click the first
                                point again or press Enter to finish.
                            </p>
                        </>
                    ) : selectedAnnotation ? (
                        <>
                            <p className="font-semibold">
                                Editing{" "}
                                {
                                    getCategory(selectedAnnotation.categoryId)
                                        .name
                                }{" "}
                                {annotations.findIndex(
                                    ({ id }) => id === selectedAnnotation.id,
                                ) + 1}
                            </p>
                            <p className="text-default-500">
                                Drag points, click an edge to add one, or select
                                a point and press Delete to remove it. With no
                                point selected, Delete removes the polygon.
                            </p>
                        </>
                    ) : (
                        <p className="text-default-500">
                            Select a polygon to edit it.
                        </p>
                    )}

                </div>
                <div className="flex flex-1 flex-col gap-1 overflow-y-auto p-2">
                    {annotations.length === 0 ? (
                        <span className="px-1 py-2 text-xs text-default-500">
                            None added yet
                        </span>
                    ) : (
                        annotations.map((annotation, index) => {
                            const category = getCategory(annotation.categoryId);
                            const isSelected =
                                annotation.id === selectedAnnotationId;
                            return (
                                <div
                                    key={annotation.id}
                                    className="flex items-center gap-1"
                                >
                                    <Button
                                        size="sm"
                                        isIconOnly
                                        aria-label={`Select polygon ${index + 1}`}
                                        aria-pressed={isSelected}
                                        color={
                                            isSelected ? "primary" : "default"
                                        }
                                        variant={
                                            isSelected ? "flat" : "bordered"
                                        }
                                        isDisabled={
                                            isEditingDisabled || isAddingPolygon
                                        }
                                        onPress={() =>
                                            selectAnnotation(annotation)
                                        }
                                    >
                                        {index + 1}
                                    </Button>
                                    <Select
                                        aria-label={`Polygon ${index + 1} type`}
                                        size="sm"
                                        variant="bordered"
                                        className="min-w-0 flex-1"
                                        disallowEmptySelection
                                        isDisabled={
                                            isEditingDisabled || isAddingPolygon
                                        }
                                        selectedKeys={[
                                            String(annotation.categoryId),
                                        ]}
                                        startContent={
                                            <span
                                                className="h-2.5 w-2.5 shrink-0 rounded-full"
                                                style={{
                                                    backgroundColor:
                                                        category.color,
                                                }}
                                            />
                                        }
                                        onChange={(event) => {
                                            const categoryId = Number(
                                                event.target.value,
                                            );
                                            if (Number.isInteger(categoryId)) {
                                                changeAnnotationCategory(
                                                    annotation.id,
                                                    categoryId,
                                                );
                                            }
                                        }}
                                    >
                                        {categoryOptions}
                                    </Select>
                                </div>
                            );
                        })
                    )}
                </div>
                <div className="flex flex-col gap-2 border-t border-default-100 p-2">
                    {renderSaveAnnotationsButton("w-full")}
                    <Button
                        size="sm"
                        color="primary"
                        variant="flat"
                        className="w-full"
                        isDisabled={
                            imageDimensions === null ||
                            validDataPolygons.length === 0 ||
                            isEditingDisabled ||
                            isAddingPolygon
                        }
                        onPress={() => addPolygons(validDataPolygons)}
                    >
                        Add full image
                    </Button>
                    <Button
                        size="sm"
                        color="primary"
                        variant="flat"
                        className="w-full"
                        isDisabled={
                            imageDimensions === null ||
                            validDataPolygons.length === 0 ||
                            isEditingDisabled ||
                            isAddingPolygon
                        }
                        onPress={fillRemainingSpace}
                    >
                        Fill remaining space
                    </Button>
                    <Button
                        size="sm"
                        color="primary"
                        className="w-full"
                        isDisabled={isEditingDisabled || isAddingPolygon}
                        onPress={startPolygon}
                        startContent={<FontAwesomeIcon icon={faPlus} />}
                    >
                        Add polygon
                    </Button>
                </div>
            </aside>

            <div className="h-full min-h-[25rem] overflow-hidden rounded-lg border border-default-300 bg-default-100 shadow-sm">
                {imageLoadFailed ? (
                    <div className="flex h-full min-h-[25rem] items-center justify-center text-sm text-danger">
                        The RGB image could not be loaded.
                    </div>
                ) : (
                    <div className="relative h-full min-h-[25rem] overflow-hidden bg-black">
                        <img
                            src={imageUrl}
                            alt={`RGB imagery ${imageId}`}
                            draggable={false}
                            className="block h-full min-h-[25rem] w-full select-none object-contain scale-x-[-1] rotate-180"
                            onLoad={(event) => {
                                const dimensions = {
                                    width: event.currentTarget.naturalWidth,
                                    height: event.currentTarget.naturalHeight,
                                };
                                imageDimensionsRef.current = dimensions;
                                setImageDimensions(dimensions);
                            }}
                            onError={() => setImageLoadFailed(true)}
                        />
                        {overlayUrl && (
                            <img
                                src={overlayUrl}
                                alt=""
                                draggable={false}
                                className="pointer-events-none absolute inset-0 h-full w-full select-none object-contain scale-x-[-1] rotate-180"
                            />
                        )}
                        {draftPoints.length > 0 && (
                            <div className="pointer-events-none absolute left-3 top-3 z-10 rounded-full bg-black/70 px-2.5 py-1 text-xs font-semibold text-white shadow-sm">
                                {selectedCategory.name} · {draftPoints.length}{" "}
                                {draftPoints.length === 1 ? "point" : "points"}
                            </div>
                        )}
                        {!isEditingDisabled &&
                            selectedAnnotationId === null &&
                            draftPoints.length === 0 && (
                                <div className="pointer-events-none absolute inset-x-0 bottom-3 z-10 flex justify-center px-3">
                                    <span className="rounded-full bg-black/70 px-3 py-1 text-xs text-white shadow-sm">
                                        {isAddingPolygon
                                            ? "Click around an affected area, then click the first point to finish."
                                            : "Choose Add polygon to start drawing."}
                                    </span>
                                </div>
                            )}
                        {imageDimensions && (
                            <svg
                                ref={annotationCanvasRef}
                                aria-label="Polygon annotation canvas"
                                tabIndex={-1}
                                className={`absolute inset-0 h-full w-full touch-none outline-none ${
                                    isEditingDisabled
                                        ? "cursor-not-allowed"
                                        : isAddingPolygon
                                          ? "cursor-crosshair"
                                          : "cursor-default"
                                }`}
                                viewBox={`0 0 ${imageDimensions.width} ${imageDimensions.height}`}
                                preserveAspectRatio="xMidYMid meet"
                                onBlur={() => setSelectedVertex(null)}
                                onKeyDown={(event) => {
                                    if (
                                        event.defaultPrevented ||
                                        event.repeat ||
                                        event.metaKey ||
                                        event.ctrlKey ||
                                        event.altKey ||
                                        event.nativeEvent.isComposing ||
                                        isEditingDisabled ||
                                        vertexDrag.current !== null
                                    ) {
                                        return;
                                    }
                                    if (
                                        event.key === "Enter" &&
                                        isAddingPolygon
                                    ) {
                                        event.preventDefault();
                                        finishPolygon();
                                        return;
                                    }
                                    const category = ANNOTATION_CATEGORIES.find(
                                        ({ id }) =>
                                            CATEGORY_SHORTCUTS[id] ===
                                            event.key.toUpperCase(),
                                    );
                                    if (category) {
                                        event.preventDefault();
                                        if (
                                            selectedAnnotation &&
                                            !isAddingPolygon
                                        ) {
                                            changeAnnotationCategory(
                                                selectedAnnotation.id,
                                                category.id,
                                            );
                                        } else {
                                            changeSelectedCategory(category.id);
                                        }
                                        return;
                                    }
                                    if (
                                        !isAddingPolygon &&
                                        (selectedVertex !== null ||
                                            selectedAnnotation !== null) &&
                                        (event.key === "Delete" ||
                                            event.key === "Backspace")
                                    ) {
                                        event.preventDefault();
                                        if (selectedVertex !== null) {
                                            deleteSelectedVertex();
                                        } else {
                                            deleteSelectedAnnotation();
                                        }
                                    }
                                }}
                                onPointerDown={(event) => {
                                    if (
                                        event.button !== 0 ||
                                        isEditingDisabled
                                    ) {
                                        return;
                                    }
                                    annotationCanvasRef.current?.focus();
                                    if (!isAddingPolygon) {
                                        setSelectedVertex(null);
                                        return;
                                    }
                                    if (isNearDraftStart(event)) {
                                        finishPolygon();
                                        return;
                                    }
                                    const point =
                                        validDataPointFromEvent(event);
                                    if (!point) return;
                                    markAnnotationRevision();
                                    setDraftPoints((currentPoints) => [
                                        ...currentPoints,
                                        point,
                                    ]);
                                    setSelectedAnnotationId(null);
                                }}
                                onPointerMove={(event) => {
                                    if (
                                        draftPoints.length === 0 ||
                                        isEditingDisabled ||
                                        !isAddingPolygon
                                    ) {
                                        return;
                                    }
                                    setPreviewPoint(
                                        isNearDraftStart(event)
                                            ? draftPoints[0]
                                            : validDataPointFromEvent(event),
                                    );
                                }}
                                onPointerLeave={() => setPreviewPoint(null)}
                            >
                                <rect
                                    width={imageDimensions.width}
                                    height={imageDimensions.height}
                                    fill="transparent"
                                />
                                {annotations.map((annotation) => {
                                    const category = getCategory(
                                        annotation.categoryId,
                                    );
                                    const isSelected =
                                        annotation.id === selectedAnnotationId;
                                    return (
                                        <polygon
                                            key={annotation.id}
                                            points={pointsToSvg(
                                                annotation.points,
                                            )}
                                            fill={category.color}
                                            fillOpacity={annotationOpacity}
                                            stroke={
                                                isSelected
                                                    ? "#facc15"
                                                    : category.color
                                            }
                                            strokeWidth={isSelected ? 3 : 2}
                                            strokeLinejoin="round"
                                            vectorEffect="non-scaling-stroke"
                                            pointerEvents={
                                                isEditingDisabled ||
                                                isAddingPolygon ||
                                                isSelected
                                                    ? "none"
                                                    : "visibleStroke"
                                            }
                                            className="cursor-pointer"
                                            onPointerDown={(event) => {
                                                if (
                                                    event.button !== 0 ||
                                                    isEditingDisabled ||
                                                    isAddingPolygon
                                                ) {
                                                    return;
                                                }
                                                event.stopPropagation();
                                                selectAnnotation(annotation);
                                            }}
                                        />
                                    );
                                })}
                                {selectedAnnotation && !isAddingPolygon && (
                                    <polygon
                                        points={pointsToSvg(
                                            selectedAnnotation.points,
                                        )}
                                        fill="none"
                                        stroke="transparent"
                                        strokeWidth={12}
                                        strokeLinejoin="round"
                                        vectorEffect="non-scaling-stroke"
                                        pointerEvents={
                                            isEditingDisabled
                                                ? "none"
                                                : "visibleStroke"
                                        }
                                        className="cursor-copy"
                                        onPointerDown={(event) => {
                                            if (
                                                event.button !== 0 ||
                                                isEditingDisabled
                                            ) {
                                                return;
                                            }
                                            event.stopPropagation();
                                            const point =
                                                pointFromClientPosition(
                                                    event.clientX,
                                                    event.clientY,
                                                    true,
                                                );
                                            if (point) {
                                                const insertedVertex =
                                                    insertVertex(
                                                        selectedAnnotation,
                                                        point,
                                                    );
                                                if (!insertedVertex) return;

                                                annotationCanvasRef.current?.focus();
                                                event.currentTarget.setPointerCapture(
                                                    event.pointerId,
                                                );
                                                const originalPoints =
                                                    insertedVertex.points.map(
                                                        (currentPoint) => ({
                                                            ...currentPoint,
                                                        }),
                                                    );
                                                vertexDrag.current = {
                                                    pointerId: event.pointerId,
                                                    annotationId:
                                                        selectedAnnotation.id,
                                                    pointIndex:
                                                        insertedVertex.pointIndex,
                                                    originalPoints,
                                                    latestPoints:
                                                        originalPoints,
                                                    hasMoved: false,
                                                };
                                            }
                                        }}
                                        onPointerMove={moveVertex}
                                        onPointerUp={(event) =>
                                            finishVertexDrag(event)
                                        }
                                        onPointerCancel={(event) =>
                                            finishVertexDrag(event, true)
                                        }
                                    />
                                )}
                                {selectedAnnotation &&
                                    !isAddingPolygon &&
                                    selectedAnnotation.points.map(
                                        (point, pointIndex) => {
                                            const category = getCategory(
                                                selectedAnnotation.categoryId,
                                            );
                                            const isSelectedVertex =
                                                selectedVertex?.annotationId ===
                                                    selectedAnnotation.id &&
                                                selectedVertex.pointIndex ===
                                                    pointIndex;

                                            return (
                                                <circle
                                                    key={`${selectedAnnotation.id}-${pointIndex}`}
                                                    cx={point.x}
                                                    cy={point.y}
                                                    r={vertexRadius}
                                                    fill={
                                                        isSelectedVertex
                                                            ? "#facc15"
                                                            : "white"
                                                    }
                                                    stroke={category.color}
                                                    strokeWidth={
                                                        isSelectedVertex ? 3 : 2
                                                    }
                                                    vectorEffect="non-scaling-stroke"
                                                    className="cursor-move"
                                                    pointerEvents={
                                                        isEditingDisabled
                                                            ? "none"
                                                            : "auto"
                                                    }
                                                    onPointerDown={(event) => {
                                                        if (
                                                            event.button !==
                                                                0 ||
                                                            isEditingDisabled
                                                        ) {
                                                            return;
                                                        }
                                                        event.stopPropagation();
                                                        annotationCanvasRef.current?.focus();
                                                        event.currentTarget.setPointerCapture(
                                                            event.pointerId,
                                                        );
                                                        selectAnnotation(
                                                            selectedAnnotation,
                                                        );
                                                        setSelectedVertex({
                                                            annotationId:
                                                                selectedAnnotation.id,
                                                            pointIndex,
                                                        });
                                                        const originalPoints =
                                                            selectedAnnotation.points.map(
                                                                (
                                                                    currentPoint,
                                                                ) => ({
                                                                    ...currentPoint,
                                                                }),
                                                            );
                                                        vertexDrag.current = {
                                                            pointerId:
                                                                event.pointerId,
                                                            annotationId:
                                                                selectedAnnotation.id,
                                                            pointIndex,
                                                            originalPoints,
                                                            latestPoints:
                                                                originalPoints,
                                                            hasMoved: false,
                                                        };
                                                    }}
                                                    onPointerMove={moveVertex}
                                                    onPointerUp={(event) =>
                                                        finishVertexDrag(event)
                                                    }
                                                    onPointerCancel={(event) =>
                                                        finishVertexDrag(
                                                            event,
                                                            true,
                                                        )
                                                    }
                                                />
                                            );
                                        },
                                    )}
                                {draftPoints.length > 0 && (
                                    <>
                                        <polyline
                                            points={pointsToSvg([
                                                ...draftPoints,
                                                ...(previewPoint
                                                    ? [previewPoint]
                                                    : []),
                                            ])}
                                            fill="none"
                                            stroke={selectedCategory.color}
                                            strokeWidth={2}
                                            strokeLinejoin="round"
                                            vectorEffect="non-scaling-stroke"
                                            pointerEvents="none"
                                        />
                                        {draftPoints.map((point, index) => (
                                            <circle
                                                key={`${point.x}-${point.y}-${index}`}
                                                cx={point.x}
                                                cy={point.y}
                                                r={
                                                    index === 0
                                                        ? vertexRadius * 1.25
                                                        : vertexRadius
                                                }
                                                fill={
                                                    index === 0
                                                        ? "white"
                                                        : selectedCategory.color
                                                }
                                                stroke={
                                                    index === 0
                                                        ? selectedCategory.color
                                                        : "white"
                                                }
                                                strokeWidth={
                                                    index === 0 ? 2 : 1.5
                                                }
                                                vectorEffect="non-scaling-stroke"
                                                pointerEvents="none"
                                            />
                                        ))}
                                        {previewPoint && (
                                            <circle
                                                cx={previewPoint.x}
                                                cy={previewPoint.y}
                                                r={vertexRadius * 0.8}
                                                fill="white"
                                                stroke={selectedCategory.color}
                                                strokeWidth={2}
                                                vectorEffect="non-scaling-stroke"
                                                pointerEvents="none"
                                            />
                                        )}
                                    </>
                                )}
                            </svg>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

export default ImageryAnnotationEditor;
